import _groupBy from 'lodash/groupBy'
import _debounce from 'lodash/debounce'

export function extractResources (response) {
  const contents = response.data
  const primaryResources = (contents.data instanceof Array ? contents.data : (contents.data == null ? [] : [contents.data]))
    .map(transformResource)

  const includedResources = (contents.included || [])
    .map(transformResource)

  const resourcesByType = _groupBy(primaryResources.concat(includedResources), r => r._type)

  const meta = contents.meta || {}

  return {
    primaryResources,
    ...resourcesByType,
    meta,
  }
}

function transformResource (resource) {
  const data = resource.attributes
  data.id = resource.id
  data._type = resource.type

  for (const relation in resource.relationships) {
    const related = resource.relationships[relation].data

    if (related instanceof Array) {
      data[relation] = related.map(r => r.id)
    } else if (related == null) {
      data[relation] = null
    } else {
      data[relation] = related.id
    }
  }

  if (resource.meta && resource.meta.permissions) {
    data._permissions = resource.meta.permissions || null
  }

  return data
}

export function extractValidationErrors (error) {
  if (error == null || error.response == null || error.response.data == null || error.response.data.errors == null) {
    console.error('Could not extract validation errors', error)
    return []
  }

  const contents = error.response.data

  return contents.errors
    .filter(e => e.status === 422)
    .map(e => ({
      message: e.message,
      source: e.source == null ? null : e.source.pointer,
      meta: e.meta,
    }))
}

export function extractValidationDetailErrors (error) {
  if (error == null || error.response == null || error.response.data == null || error.response.data.errors == null) {
    console.error('Could not extract validation errors', error)
    return []
  }

  const contents = error.response.data

  return contents.errors
    .filter(e => e.status === 422)
    .map(e => {
      const errors = []
      if (e.details !== null && typeof e.details === 'object' && !Array.isArray(e.details)) {
        for (const [key, value] of Object.entries(e.details)) {
          errors.push({
            message: value,
            source: `data/attributes/${key}`,
          })
        }
      }
      return errors
    }).flat()
}

/**
 * Creates a debounced fetcher, which merges multiple resource requests into one
 * @param  {([ids], ...) => Promise} fetch   A fetch method, which should fetch the given resource ids and return a Promise (first argument must be array of ids)
 * @param  {int} timeout              Delay to wait for mergable requests
 * @param  {int} max                  If the combined number of ids is larger than max, then the fetch will fire immediately instead of waiting for timeout
 * @return {([ids]) => Promise}         The returned promise is resolved when the merged fetch request is finished
 */
export function createResourceFetcher (fetch, timeout, max) {
  let resolver = null
  let rejector = null

  let promise = new Promise((resolve, reject) => {
    resolver = resolve
    rejector = reject
  })

  let allIds = new Set()

  const debouncedFetch = _debounce((...args) => {
    const ids = [...allIds]
    const currentResolver = resolver
    const currentRejector = rejector

    // prepare for next run
    allIds = new Set()
    promise = new Promise((resolve, reject) => {
      resolver = resolve
      rejector = reject
    })

    args.unshift(ids) // prepend ids argument

    fetch.apply(this, args)
      .then(currentResolver, currentRejector)
  }, timeout)

  const fetcher = (ids, ...args) => {
    ids.forEach(id => allIds.add('' + id))
    debouncedFetch.apply(this, args)
    if (max && allIds.size >= max) {
      debouncedFetch.flush()
    }
    return promise
  }

  // proxy debounce methods
  fetcher.flush = debouncedFetch.flush
  fetcher.cancel = debouncedFetch.cancel

  return fetcher
}
